import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Target } from 'lucide-react'
import { DateTime } from 'luxon'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useDismissGoalReview, useGetGoalReview } from '../../services/api/goals.hooks'
import Log from '../../services/api/log'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { reviewNudge } from './goalReviewRules'

/**
 * The weekly review's entry on the Daily Overview, in the slot and the markup NoGoalNudge uses. The
 * two never show together: NoGoalNudge speaks when there is no live goal, this one needs at least
 * one to walk.
 *
 * "Not this week" is words rather than an X so it cannot be read as dismissing the review for good,
 * and it is saved with the review week on the server, so the card returns with the next one.
 *
 * Whether it shows, and what it says, lives in reviewNudge(); this component only draws it.
 */
const GoalReviewNudge = () => {
    const { allGoals, areGoalsLoading, areGoalsUnavailable } = useGoalCreation()
    const navigate = useNavigate()
    const { data: review } = useGetGoalReview()
    const { mutate: dismiss, isLoading: isDismissing } = useDismissGoalReview()
    const isReadOnly = useIsReadOnly()

    const nudge = reviewNudge({
        goals: allGoals,
        review,
        // A failed goals request leaves the list empty, not known to be empty: say nothing.
        areGoalsLoading: areGoalsLoading || areGoalsUnavailable,
        now: DateTime.local(),
    })
    if (!nudge) return null

    const isDone = nudge.kind === 'done'

    return (
        <div
            style={{ animationFillMode: 'both' }}
            className={cn(
                'mb-2 rounded-gt-md border px-3 py-2.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-300',
                isDone ? 'border-hairline bg-card' : 'border-border bg-background'
            )}
            data-testid="goal-review-nudge"
        >
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-gt-violet/10">
                    <Target className="size-3.5 text-gt-violet" />
                </div>
                <div className="min-w-[11rem] flex-1">
                    <h3 className="text-title-sm text-foreground">{nudge.title}</h3>
                    <p className="text-pretty text-body-sm text-muted-foreground">{nudge.body}</p>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-1">
                    {nudge.kind === 'done' ? (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground"
                            onClick={() => navigate('/goals/review')}
                        >
                            Summary
                        </Button>
                    ) : (
                        <>
                            <Button
                                size="sm"
                                disabled={isReadOnly}
                                onClick={() => {
                                    Log(nudge.kind === 'continue' ? 'goal_review_continued' : 'goal_review_started')
                                    navigate('/goals/review')
                                }}
                                className="bg-action text-action-foreground transition-transform hover:bg-action-hover active:scale-[0.96]"
                            >
                                {nudge.action}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={isDismissing || isReadOnly}
                                onClick={() => {
                                    Log('goal_review_dismissed')
                                    dismiss()
                                }}
                                className="text-muted-foreground transition-transform active:scale-[0.96]"
                            >
                                Not this week
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default GoalReviewNudge
